//@ts-check
import React, { useCallback } from 'react';
import { Box, Card, CardContent, Typography, Stack, Divider, Button, Grid } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import PrintIcon from '@mui/icons-material/Print';
import FeeDisplay from '../FeeDisplay/FeeDisplay';
import commonStrings from '../../constants/commonStrings';
import PropTypes from 'prop-types';
import FlexLib from '@flex/lib';

/** @typedef {import('../../../../types/PaymentConfirmationV2').PaymentConfirmationV2Props} PaymentConfirmationV2Props */
/** @typedef {import('../../../../types/PaymentConfirmationV2').PaymentResponseData} PaymentResponseData */
/** @typedef {import('../../../../types/fees').PaymentType} PaymentType */

const NOT_AVAILABLE = 'N/A';

/** @type {Record<PaymentType, string>} */
const PAYMENT_TYPE_DISPLAY = {
  credit: 'Credit Card',
  debit: 'Debit Card',
  ach: 'Bank Transfer',
  cash: 'Cash',
  '': '',
};

/**
 * @component PaymentConfirmationV2
 * @description Displays payment confirmation details after successful transaction.
 *
 * @param {PaymentConfirmationV2Props} props - Component props
 * @returns {JSX.Element} Payment confirmation display
 */
const PaymentConfirmationV2 = ({
  invoiceNumber,
  paymentResponse,
  paymentData,
  customerName,
  calculationResult,
  onDownloadInvoice,
  locationName,
  locationImageUrl,
  feeSettings,
}) => {
  /** @type {PaymentType} */
  const paymentType = paymentData?.paymentMethod?.paymentType;

  /**
   * @description Generate receipt HTML and open in print dialog
   * Uses ReceiptService to generate HTML receipt with inline styles
   */
  const handlePrintReceipt = useCallback(() => {
    try {
      const { ReceiptService } = FlexLib.services;

      /** @type {import('../../../../flex-lib/services/ReceiptService').ReceiptData} */
      const receiptData = {
        invoiceNumber,
        paymentResponse,
        paymentData,
        customerName,
        calculationResult,
        locationName,
        locationImageUrl,
        feeSettings,
      };

      const receipt = new ReceiptService.Receipt(receiptData);
      const htmlString = receipt.buildHTML();

      const printWindow = window.open('', 'receipt-print', 'width=800,height=600');
      if (printWindow) {
        printWindow.document.write(htmlString);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
      }
    } catch (error) {
      console.error('Error printing receipt:', error);
    }
  }, [
    invoiceNumber,
    paymentResponse,
    paymentData,
    customerName,
    calculationResult,
    locationName,
    locationImageUrl,
    feeSettings,
  ]);

  /**
   * @description Format date to MM/DD/YYYY using native toLocaleDateString
   * @returns {string} Formatted date string
   */
  const getFormattedDate = () => {
    return new Date().toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  /**
   * @description Get payment type display text
   * If Card, Card Carrier
   * If Card but no carrier, Credit or Debit
   * If ACH, Bank Transfer (ACH)
   *
   * @returns {string} Display text for payment type
   */
  const getPaymentTypeDisplay = () => {
    const creditOrDebit = new Set([commonStrings.credit, commonStrings.debit]);
    if (creditOrDebit.has(paymentType) && paymentData?.paymentMethod?.binCodesResponse?.card) {
      // Carrier 'VISA', 'MASTERCARD', etc.
      return paymentData.paymentMethod.binCodesResponse.card.toUpperCase();
    } else if (creditOrDebit.has(paymentType)) {
      // 'Credit Card' or 'Debit Card'
      return paymentType === commonStrings.credit ? PAYMENT_TYPE_DISPLAY.credit : PAYMENT_TYPE_DISPLAY.debit;
    } else if (paymentType === commonStrings.ach) {
      // 'Bank Transfer' for ACH,
      return PAYMENT_TYPE_DISPLAY.ach;
    } else {
      return NOT_AVAILABLE;
    }
  };

  const handleDownloadInvoice = () => {
    if (onDownloadInvoice) {
      onDownloadInvoice();
    }
  };

  /**
   * @description Format customer name
   * @param {string} name - Full customer name
   * @returns {string} Formatted name
   */
  const formatName = (name) => {
    if (!name) return NOT_AVAILABLE;
    return name
      .split(' ')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ')
      .trim();
  };

  /**
   * @description Puts payment Type in Parenthises or returns empty string if no payment type
   * @returns {string}
   */
  const formatAccountNumberLabel = () => {
    if (!paymentType) return '';
    if (paymentType === commonStrings.ach) {
      return `(${paymentType.toUpperCase()})`;
    }
    return `(${paymentType[0].toUpperCase()}${paymentType.slice(1)})`;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
        p: 2,
      }}
      data-cy="PaymentConfirmationV2-container"
    >
      <Card
        sx={{
          maxWidth: 800,
          width: '100%',
          boxShadow: 3,
        }}
        data-cy="PaymentConfirmationV2-card"
      >
        <CardContent sx={{ p: 4 }}>
          {/* Success Header */}
          <Stack
            direction="column"
            alignItems="center"
            spacing={2}
            sx={{ mb: 4 }}
            data-cy="PaymentConfirmationV2-header"
          >
            <CheckCircleOutlineIcon
              sx={{
                fontSize: 80,
                color: 'success.main',
              }}
              data-cy="PaymentConfirmationV2-success-icon"
            />
            <Typography
              variant="h4"
              component="h1"
              fontWeight="bold"
              textAlign="center"
              data-cy="PaymentConfirmationV2-title"
            >
              Payment Successful
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              textAlign="center"
              data-cy="PaymentConfirmationV2-subtitle"
            >
              Your payment has been processed successfully
            </Typography>
          </Stack>

          <Divider sx={{ mb: 3 }} />

          {/* Transaction Details */}
          <Stack spacing={2.5} sx={{ mb: 4 }}>
            <Typography variant="h6" fontWeight="bold" data-cy="PaymentConfirmationV2-details-title">
              Transaction Details
            </Typography>

            <Grid container spacing={2}>
              {invoiceNumber && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" data-cy="PaymentConfirmationV2-invoice-label">
                    Invoice Number
                  </Typography>
                  <Typography variant="body1" fontWeight="medium" data-cy="PaymentConfirmationV2-invoice-value">
                    {invoiceNumber}
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary" data-cy="PaymentConfirmationV2-transaction-id-label">
                  Transaction ID
                </Typography>
                <Typography variant="body1" fontWeight="medium" data-cy="PaymentConfirmationV2-transaction-id-value">
                  {paymentResponse?.transactionId || NOT_AVAILABLE}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary" data-cy="PaymentConfirmationV2-customer-label">
                  Customer Name
                </Typography>
                <Typography variant="body1" fontWeight="medium" data-cy="PaymentConfirmationV2-customer-value">
                  {formatName(customerName) || NOT_AVAILABLE}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary" data-cy="PaymentConfirmationV2-payment-type-label">
                  Payment Type
                </Typography>
                <Typography variant="body1" fontWeight="medium" data-cy="PaymentConfirmationV2-payment-type-value">
                  {getPaymentTypeDisplay()}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary" data-cy="PaymentConfirmationV2-account-label">
                  Account Number {formatAccountNumberLabel()}
                </Typography>
                <Typography variant="body1" fontWeight="medium" data-cy="PaymentConfirmationV2-account-value">
                  {paymentData?.paymentMethod?.maskedAccountNumber || NOT_AVAILABLE}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary" data-cy="PaymentConfirmationV2-date-label">
                  Date
                </Typography>
                <Typography variant="body1" fontWeight="medium" data-cy="PaymentConfirmationV2-date-value">
                  {getFormattedDate() || NOT_AVAILABLE}
                </Typography>
              </Grid>
            </Grid>
          </Stack>

          <Divider sx={{ mb: 3 }} />

          {/* Fee Breakdown */}
          {calculationResult && (
            <Box sx={{ mb: 4 }} data-cy="PaymentConfirmationV2-fee-breakdown">
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }} data-cy="PaymentConfirmationV2-fee-title">
                Payment Breakdown
              </Typography>
              <FeeDisplay calculate={false} calculationResult={calculationResult} />
            </Box>
          )}

          <Divider sx={{ mb: 3 }} />

          {/* Download Buttons */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
            data-cy="PaymentConfirmationV2-actions"
          >
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handlePrintReceipt}
              data-cy="PaymentConfirmationV2-print-receipt"
              sx={{ minWidth: 200 }}
            >
              Print Receipt
            </Button>
            <Button
              variant="outlined"
              startIcon={<DescriptionIcon />}
              onClick={handleDownloadInvoice}
              disabled={!onDownloadInvoice}
              data-cy="PaymentConfirmationV2-download-invoice"
              sx={{ minWidth: 200 }}
            >
              Download Invoice
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

PaymentConfirmationV2.propTypes = {
  invoiceNumber: PropTypes.string,
  paymentResponse: PropTypes.shape({
    message: PropTypes.string.isRequired,
    transactionId: PropTypes.string.isRequired,
  }).isRequired,
  paymentData: PropTypes.shape({
    paymentMethod: PropTypes.shape({
      maskedAccountNumber: PropTypes.string,
      paymentType: PropTypes.string,
      binCodesResponse: PropTypes.shape({
        card: PropTypes.string,
      }),
    }).isRequired,
  }).isRequired,
  customerName: PropTypes.string.isRequired,
  calculationResult: PropTypes.shape({
    totalAmount: PropTypes.string,
    fees: PropTypes.object,
    baseAmount: PropTypes.string,
    state: PropTypes.string,
    paymentType: PropTypes.string,
    options: PropTypes.object,
    subTotal: PropTypes.string,
  }),
  onDownloadInvoice: PropTypes.func,
  locationName: PropTypes.string,
  locationImageUrl: PropTypes.string,
  feeSettings: PropTypes.objectOf(
    PropTypes.shape({
      customLabel: PropTypes.string,
    })
  ),
};

export default PaymentConfirmationV2;
